import type { DataHealth, HealthStatus } from '@growth/client';
import { useState } from 'react';
import { useParams } from 'react-router';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { Card, ErrorBox, JsonView, StatusBadge } from '../components/ui.tsx';
import { HEALTH_TITLE } from '../alerts.ts';
import { useGlobalFilters } from '../filters.ts';
import { fmtInt, fmtNum } from '../format.ts';
import { useApi } from '../session.tsx';
import { FreshnessLine } from './Overview.tsx';

const ORDER: HealthStatus[] = ['error', 'warn', 'info', 'ok'];

export function HealthPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const range = { environment: filters.params.environment, last: filters.params.last, from: filters.params.from, to: filters.params.to };
  const key = `health:${gameId}:${JSON.stringify(range)}`;
  const health = useApi<DataHealth>(key, (api, s) => api.dataHealth(gameId, range, s), { refreshMs: 60_000 });
  const [open, setOpen] = useState<string | null>(null);
  const h = health.data;
  const checks = [...(h?.checks ?? [])].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status));

  return (
    <>
      <PageHeader
        title="Data health"
        sub={<FreshnessLine fresh={health.data?.fresh} />}
        right={
          h && (
            <span className="row" aria-label="Resumen de comprobaciones">
              {ORDER.map((s) => (
                <span key={s} className="row" style={{ gap: 4 }}>
                  <StatusBadge status={s} /> {fmtInt(h.summary[s])}
                </span>
              ))}
            </span>
          )
        }
      />
      <FilterBar filters={filters} dimensions={false} compare={false} />
      <p className="muted small" style={{ marginTop: -6 }}>
        Antes de decidir nada con los datos, todo debería estar en OK. Los avisos explican qué mirar y muestran ejemplos.
      </p>
      {health.error && <ErrorBox error={health.error} onRetry={health.reload} />}
      <div className={`stack loading-frame${health.loading && h ? ' stale' : ''}`}>
        {checks.map((c) => (
          <Card
            key={c.id}
            title={
              <span className="row">
                <StatusBadge status={c.status} /> {HEALTH_TITLE[c.id] ?? c.title}
              </span>
            }
            subtitle={<code>{c.id}</code>}
            actions={
              c.examples && c.examples.length > 0 ? (
                <button type="button" className="btn small ghost" aria-expanded={open === c.id} onClick={() => setOpen(open === c.id ? null : c.id)}>
                  {open === c.id ? 'Ocultar ejemplos' : `Ver ${c.examples.length} ejemplos`}
                </button>
              ) : undefined
            }
          >
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ flex: 1, minWidth: 240 }}>{c.detail}</span>
              {c.value !== null && <strong style={{ fontSize: 18 }}>{Number.isInteger(c.value) ? fmtInt(c.value) : fmtNum(c.value, 4)}</strong>}
            </div>
            {open === c.id && c.examples && (
              <div style={{ marginTop: 10 }}>
                <JsonView value={c.examples} />
              </div>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}
